/**
 * Lo que el enlace de renovacion le CUENTA al cliente sobre ID.me.
 *
 * Lissette, 2026-09-09: *"la pregunta de ID que sea obligatorio; si dice que
 * si, preguntar si el correo que uso para ID es el mismo que tenemos en
 * nuestros archivos y colocar el correo del titular; y si dice no, mandar un
 * aviso «vea aqui como hacerlo»; y colocar «si tienes alguna duda creando tu
 * cuenta ID puedes contactarnos para darte mas orientacion» — escribelo de
 * manera que no se vea incumpliendo su privacidad"*.
 *
 * POR QUE LOS PASOS ESTAN AQUI Y NO SON UNA IMAGEN. Lissette mando una lamina
 * en PDF con los pasos, pero la hizo Blue Cross (viene escrito dentro del
 * propio archivo): publicar el material de otra compañia en una pagina que
 * lleva el logo de la agencia no es algo que se haga sin pedirlo. Los pasos son
 * los mismos, escritos aqui, y ademas asi se leen bien en un telefono, se
 * traducen y se cambian sin volver a exportar nada.
 *
 * Este modulo no toca React ni el servidor a proposito: es texto y una regla,
 * y asi se puede probar entero.
 */

/** La pagina oficial donde se crea la cuenta. */
export const ENLACE_OFICIAL = 'https://www.id.me/';

/** Tope de un correo. 254 es el maximo real de una direccion. ESPEJO de
 *  LARGO_MAXIMO_CORREO en deskte-api/src/routes/renewal-review.routes.ts. */
export const LARGO_MAXIMO_CORREO = 254;

/**
 * Un texto con pinta de correo: algo, arroba, algo, punto, algo, sin espacios.
 *
 * A proposito flojo: desde aqui no se puede saber si el buzon existe. Lo unico
 * que hace es impedir que se mande "no me acuerdo" como si fuera un correo.
 *
 * ESPEJO de `pareceUnCorreo` en renewal-review.routes.ts — el servidor lo
 * vuelve a comprobar porque el enlace es publico y el envio lo escribe quien
 * quiera. Si cambia una regla, cambian las dos.
 */
export function correoValido(texto) {
  const v = String(texto || '').trim();
  if (!v || v.length > LARGO_MAXIMO_CORREO) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/**
 * La LAMINA de Lissette: la guia entera en una imagen, la que ella hizo y usa
 * con sus clientes.
 *
 * Lissette, 2026-09-09: *"no me gusto como sale la imagen de como hacer el ID,
 * no tiene imagen nada; ese PDF lo hice yo, coloca la imagen tal como esta,
 * como foto, cuando le dan No"*.
 *
 * Va como .webp en crm-app/public/ayuda/, que es donde ya viven las capturas
 * del centro de ayuda. Solo se descarga cuando el cliente contesta que NO
 * tiene cuenta: hasta entonces la etiqueta <img> ni existe.
 *
 * DE MOMENTO SOLO EN ESPANOL, y por eso hay una lista y no una plantilla de
 * nombre: pedir "idme-guia-en.webp" cuando no existe deja un icono de imagen
 * rota en la cara del cliente. Cuando haya version en ingles, se suelta el
 * archivo con ese nombre, se anade 'en' aqui, y ya.
 */
export const IDIOMAS_CON_LAMINA = ['es'];

/** La ruta de la lamina para ese idioma, dentro de crm-app/public. */
export function laminaDeIdMe(lang) {
  const idioma = IDIOMAS_CON_LAMINA.includes(lang) ? lang : 'es';
  return `ayuda/idme-guia-${idioma}.webp`;
}

/**
 * Los pasos para crear la cuenta, en el orden en que se hacen.
 *
 * Son los mismos de la lamina, escritos. NO sobran por tener la imagen: la
 * lamina esta solo en espanol, pesa y hay que ampliarla para leer la letra
 * pequena; el texto se lee de corrido en el movil, se puede copiar, lo lee un
 * lector de pantalla, y sigue ahi si la imagen no carga.
 */
export const PASOS_ID_ME = [
  {
    es: 'Ten a mano tu correo personal y tu teléfono.',
    en: 'Have your personal email and your phone with you.',
  },
  {
    es: 'Si ya tienes cuenta, no crees otra: inicia sesión con la que tienes.',
    en: "If you already have an account, don't create another one: sign in with it.",
  },
  {
    es: 'Entra a ID.me y pulsa «Create an account» / «Crear cuenta».',
    en: 'Go to ID.me and tap "Create an account".',
  },
  {
    es: 'Escribe tu correo y crea una contraseña (mínimo 8 caracteres, con una mayúscula, una minúscula y un número o símbolo).',
    en: 'Enter your email and create a password (at least 8 characters, with an uppercase, a lowercase and a number or symbol).',
  },
  {
    es: 'Abre tu correo y confirma el mensaje que te llega de hello@id.me. Si no lo ves, mira en Spam o Correo no deseado.',
    en: 'Open your email and confirm the message from hello@id.me. If you don’t see it, check Spam or Junk.',
  },
  {
    es: 'Elige cómo quieres recibir tus códigos de seguridad: mensaje de texto, llamada o una aplicación.',
    en: 'Choose how you want to receive your security codes: text message, phone call or an app.',
  },
  {
    es: 'Verifica tu identidad: foto de tu identificación por delante (y por detrás si te lo piden) y una foto de tu cara.',
    en: 'Verify your identity: a photo of your ID front (and back if asked) and a photo of your face.',
  },
  {
    es: 'Revisa que tus datos estén bien y continúa.',
    en: 'Check that your information is correct and continue.',
  },
];

/** Trucos para que la verificación salga a la primera. */
export const CONSEJOS_ID_ME = [
  { es: 'Busca buena luz.', en: 'Find good lighting.' },
  { es: 'Que se vean las cuatro esquinas de la identificación.', en: 'Make sure all four corners of the ID are visible.' },
  { es: 'Quítate gafas o gorra si te lo piden.', en: 'Take off glasses or a hat if asked.' },
];

/**
 * El ofrecimiento de ayuda, redactado para que NO parezca que le vamos a
 * entrar en la cuenta.
 *
 * Lissette pidio expresamente que se escribiera *"de manera que no se vea
 * incumpliendo su privacidad"*. Por eso dice tres cosas y en este orden: que le
 * acompañamos, que la cuenta es SUYA, y que no le vamos a pedir ni la
 * contraseña ni los codigos. Es lo mismo que dice al pie la propia guia que
 * ella usa ("no compartas tu contraseña ni tus codigos con nadie"; "el agente
 * te guia, tu controlas tu cuenta") — dicho por nosotros, antes de que le haga
 * falta preguntarlo.
 */
export const AVISO_DE_AYUDA = {
  es: 'Si tienes dudas creando tu cuenta, escríbenos o llámanos y te orientamos paso a paso. '
    + 'La cuenta es tuya y la creas tú: nunca te vamos a pedir tu contraseña ni los códigos de seguridad que te lleguen.',
  en: 'If you have questions creating your account, write or call us and we will walk you through it. '
    + 'The account is yours and you create it: we will never ask you for your password or the security codes you receive.',
};

/** El texto en el idioma del cliente, sin repetir el ternario en cada sitio. */
export function enIdioma(par, lang) {
  if (!par) return '';
  return lang === 'en' ? (par.en || par.es || '') : (par.es || par.en || '');
}
